import { makeBaseEntitiesState, makeDefaultEntitiesState } from '@modules/app-common/state/entities.state';
import { Action, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {
  FindCareerSurveyResultsGQL,
  FindCareerSurveyResultsTeacherClassroomGQL,
  FindCareerSurveyResultsTeacherClassroomQuery,
  FindCareerSurveyResultsMineGQL,
  FindCareerSurveyResultsMineQuery,
  CareerSurveyResult,
  FindCareerSurveyResultsQuery,
  UpdateCareerSurveyResultGQL,
  CreateCareerSurveyResultGQL,
  UpdateCareerSurveyResultMutation,
  CreateCareerSurveyResultMutation,
  SaveCareerSurveyResultAnswerGQL,
} from 'generated/graphql';
import { concat, tap, mergeMap, from, of, iif, defer, lastValueFrom } from 'rxjs';
import { NetworkCallService } from '@common/services';

export class CareerSurveyResultsFindManyAction {
  static readonly type = '[Career Survey Results] Find Many';

  constructor() {}
}

export class CareerSurveyResultsFindMineAction {
  static readonly type = '[Career Survey Results] Find Mine (Students)';

  constructor(public payload: { createIfNotFound: boolean } = { createIfNotFound: false }) {}
}

export class CareerSurveyResultsFindTeacherClassroomAction {
  static readonly type = "[Career Survey Results] Find Teacher' Classroom's Results";

  constructor(public payload: { classroomId: string }) {}
}

export class CareerSurveyResultsGetStudentsAction {
  static readonly type = '[Career Survey Results] Get By Student Id';

  constructor(public payload: { studentId: string }) {}
}

export class CareerSurveyResultsSaveAction {
  static readonly type = '[Career Survey Results] Save';

  constructor(public payload: { careerSurveyResult: Partial<CareerSurveyResult> }) {}
}

export class CareerSurveyResultsSaveAnswersAction {
  static readonly type = '[Career Survey Results] Save Answers';

  constructor(public payload: { careerSurveyResult: Pick<CareerSurveyResult, '_id' | 'answers'> }) {}
}

export class CareerSurveyResultsCreateAction {
  static readonly type = '[Career Survey Results] Create';
}

const CareerSurveyResultsBaseEntitiesState = makeBaseEntitiesState<CareerSurveyResult>('careerSurveyResults');

@State({
  name: 'careerSurveyResults',
  defaults: makeDefaultEntitiesState(),
})
@Injectable()
export class CareerSurveyResultsEntitiesState extends CareerSurveyResultsBaseEntitiesState {
  ncs = inject(NetworkCallService);
  findCareerSurveyResultsGQL = inject(FindCareerSurveyResultsGQL);
  findCareerSurveyResultsTeacherClassroomGQL = inject(FindCareerSurveyResultsTeacherClassroomGQL);
  findCareerSurveyResultsMineGQL = inject(FindCareerSurveyResultsMineGQL);
  updateCareerSurveyResultsGQL = inject(UpdateCareerSurveyResultGQL);
  createCareerSurveyResultsGQL = inject(CreateCareerSurveyResultGQL);
  saveCareerSurveyResultAnswerGQL = inject(SaveCareerSurveyResultAnswerGQL);

  @Action(CareerSurveyResultsFindManyAction)
  find(ctx: StateContext<any>, action: CareerSurveyResultsFindManyAction) {
    return this.ncs.queryInBackground(() =>
      this.findCareerSurveyResultsGQL
        .fetch({ dto: {} }, { fetchPolicy: 'no-cache' })
        .pipe(this.patchEntitiesFromQueryResult<FindCareerSurveyResultsQuery>('findCareerSurveyResults', ctx))
    );
  }

  @Action(CareerSurveyResultsFindMineAction)
  async findMine(ctx: StateContext<any>, action: CareerSurveyResultsFindMineAction) {
    const result = await lastValueFrom(
      this.ncs.queryInBackground(() =>
        this.findCareerSurveyResultsMineGQL
          .fetch({}, { fetchPolicy: 'no-cache' })
          .pipe(this.patchEntitiesFromQueryResult<FindCareerSurveyResultsMineQuery>('findMyCareerSurveyResults', ctx))
      )
    );

    if (result && result.data && result.data.findMyCareerSurveyResults.length === 0 && action.payload.createIfNotFound) {
      ctx.dispatch(new CareerSurveyResultsCreateAction());
    }
  }

  @Action(CareerSurveyResultsFindTeacherClassroomAction)
  findTeacherClassroomResults(ctx: StateContext<any>, action: CareerSurveyResultsFindTeacherClassroomAction) {
    return this.ncs.queryInBackground(() =>
      this.findCareerSurveyResultsTeacherClassroomGQL
        .fetch(
          {
            dto: { classroomId: action.payload.classroomId },
          },
          { fetchPolicy: 'no-cache' }
        )
        .pipe(this.patchEntitiesFromQueryResult<FindCareerSurveyResultsTeacherClassroomQuery>('findTeacherClassroomCareerSurveyResults', ctx))
    );
  }

  @Action(CareerSurveyResultsGetStudentsAction)
  getSurveyResultByUserId(ctx: StateContext<any>, action: CareerSurveyResultsGetStudentsAction) {
    return this.ncs.queryInBackground(() =>
      this.findCareerSurveyResultsGQL.fetch({ dto: { userId: action.payload.studentId } }, { fetchPolicy: 'no-cache' }).pipe(
        tap((result) => {
          this.patchEntities(ctx, result.data.findCareerSurveyResults as CareerSurveyResult[]);
        })
      )
    );
  }

  @Action(CareerSurveyResultsCreateAction)
  createCareerSurveyResult(ctx: StateContext<any>, action: CareerSurveyResultsCreateAction) {
    return this.ncs.mutateInBackground(() =>
      this.createCareerSurveyResultsGQL
        .mutate({ dto: {} }, { context: { ignoreLoading: true } })
        .pipe(this.patchEntityFromMutationResult<CreateCareerSurveyResultMutation>('createCareerSurveyResult', ctx))
    );
  }

  @Action(CareerSurveyResultsSaveAction)
  async save(ctx: StateContext<any>, action: CareerSurveyResultsSaveAction) {
    const saveData = action.payload.careerSurveyResult;
    const isUpdate = !!saveData._id;

    if (isUpdate) {
      const dto: any = {
        _id: saveData._id,
        houseKey: saveData.houseKey,
        threadKey: saveData.threadKey,
        preCareerInterestKeys: saveData.preCareerInterestKeys,
        postCareerInterestKeys: saveData.postCareerInterestKeys,
        completed: saveData.completed || false,
        completedAt: saveData.completedAt || null,
      };

      await lastValueFrom(
        this.ncs.mutateInBackground(() =>
          this.updateCareerSurveyResultsGQL
            .mutate(
              {
                dto,
              },
              { context: { ignoreLoading: true } }
            )
            .pipe(this.patchEntityFromMutationResult<UpdateCareerSurveyResultMutation>('updateCareerSurveyResult', ctx))
        )
      );

      if (saveData.answers?.length) {
        ctx.dispatch(
          new CareerSurveyResultsSaveAnswersAction({
            careerSurveyResult: {
              _id: saveData._id,
              answers: saveData.answers!,
            },
          })
        );
      }
    } else {
      const dto = {
        houseKey: saveData.houseKey,
        threadKey: saveData.threadKey,
        preCareerInterestKeys: saveData.preCareerInterestKeys,
        postCareerInterestKeys: saveData.postCareerInterestKeys,
      };
      return this.ncs.mutateInBackground(() =>
        this.createCareerSurveyResultsGQL
          .mutate(
            {
              dto,
            },
            { context: { ignoreLoading: true } }
          )
          .pipe(this.patchEntityFromMutationResult<CreateCareerSurveyResultMutation>('createCareerSurveyResult', ctx))
      );
    }
  }

  @Action(CareerSurveyResultsSaveAnswersAction)
  async saveAnswers(ctx: StateContext<any>, action: CareerSurveyResultsSaveAnswersAction) {
    // GraphQL mutation is designed to save one answer at a time. For convenience the state action accepts array of answers and each one is saved sequentially.
    // future optimization
    const csr = action.payload.careerSurveyResult;

    for (const answer of csr.answers) {
      await lastValueFrom(
        this.ncs.mutateInBackground(() =>
          this.saveCareerSurveyResultAnswerGQL
            .mutate(
              {
                dto: {
                  _id: csr._id,
                  qId: answer.qId,
                  answerId: answer.answerId,
                },
              },
              { context: { ignoreLoading: true } }
            )
            .pipe(this.patchEntityFromMutationResult('saveCareerSurveyResultAnswer', ctx))
        )
      );
    }
  }
}
